import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { ArrowLeft, Briefcase, FileText, CalendarOff, Coins, Award, GraduationCap, AlertTriangle, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import FileAttach, { AttachmentList } from "../../components/FileAttach";
import { localName, localFullName } from "../../lib/localName";

function MiniDialog({ trigger, title, fields, onSubmit, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm({}); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(f => (
            <div key={f.name}>
              <Label>{f.label}</Label>
              {f.type === "textarea"
                ? <Textarea rows={3} value={form[f.name] || ""} onChange={e => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${testIdPrefix}-${f.name}`} />
                : f.type === "select"
                  ? <Select value={form[f.name] || ""} onValueChange={v => setForm({ ...form, [f.name]: v })}>
                      <SelectTrigger data-testid={`${testIdPrefix}-${f.name}`}><SelectValue placeholder={f.label} /></SelectTrigger>
                      <SelectContent>{f.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  : f.type === "file"
                    ? <FileAttach
                        value={form[f.name] ? [{ id: form[f.name], name: f.label }] : []}
                        onChange={(list) => setForm({ ...form, [f.name]: list[list.length - 1]?.id || null })}
                        testIdPrefix={`${testIdPrefix}-${f.name}`}
                      />
                    : <Input type={f.type || "text"} value={form[f.name] || ""} onChange={e => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${testIdPrefix}-${f.name}`} />}
            </div>
          ))}
          <Button onClick={async () => { await onSubmit(form); setOpen(false); }} className="w-full" data-testid={`${testIdPrefix}-submit`}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EmployeeDetailPage() {
  const {  t , lang } = useI18n();
  const { eid } = useParams();
  const [emp, setEmp] = useState(null);
  const [balance, setBalance] = useState(null);

  const load = useCallback(() => {
    api.get(`/employees/${eid}`).then(r => setEmp(r.data));
    api.get(`/leave-balance/${eid}`).then(r => setBalance(r.data)).catch(() => {});
  }, [eid]);
  useEffect(() => { load(); }, [load]);

  if (!emp) return <div className="p-8 text-muted-foreground">...</div>;

  const addContract = async (f) => { await api.post("/employee-contracts", { ...f, employee_id: eid, salary: Number(f.salary) || 0 }); toast.success(t("success")); load(); };
  const addLeave = async (f) => { await api.post("/leave-requests", { ...f, employee_id: eid }); toast.success(t("success")); load(); };
  const decideLeave = async (id, status) => { await api.put(`/leave-requests/${id}/decide`, { status }); toast.success(t("success")); load(); };
  const addAttendance = async (f) => { await api.post("/hr-attendance", { ...f, employee_id: eid, late_min: Number(f.late_min) || 0 }); toast.success(t("success")); load(); };
  const addPay = async (f) => {
    await api.post("/payroll", { employee_id: eid, period: f.period, base_salary: Number(f.base_salary) || emp.base_salary,
      allowances: f.allowances_value ? { housing: Number(f.allowances_value) } : {},
      deductions: f.deductions_value ? { misc: Number(f.deductions_value) } : {}, overtime: Number(f.overtime || 0) });
    toast.success(t("success")); load();
  };
  const addReview = async (f) => { await api.post("/performance-reviews", { ...f, employee_id: eid, score: Number(f.score) || 0 }); toast.success(t("success")); load(); };
  const addTraining = async (f) => { await api.post("/hr-trainings", { ...f, employee_id: eid, cost: Number(f.cost) || 0 }); toast.success(t("success")); load(); };
  const addViolation = async (f) => { await api.post("/violations", { ...f, employee_id: eid, fine_amount: f.fine_amount ? Number(f.fine_amount) : null }); toast.success(t("success")); load(); };

  return (
    <div className="space-y-6">
      <Link to="/hr/employees" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-4 h-4 lucide-rotate-rtl" /> {t("employees")}</Link>

      <Card className="p-6 border shadow-none">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl bg-primary/10 text-primary grid place-items-center"><Briefcase className="w-8 h-8" /></div>
          <div className="flex-1">
            <div className="text-2xl font-semibold">{localFullName(emp, lang)}</div>
            <div className="text-sm text-muted-foreground">{t(emp.position) || emp.position} · {t(emp.department) || emp.department} · {emp.base_salary} SAR</div>
            <div className="text-xs text-muted-foreground mt-1 font-mono">{emp.national_id || ""} · {emp.phone || ""}</div>
          </div>
          <span className={`text-xs px-3 py-1 rounded-full ${emp.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30" : "bg-muted text-muted-foreground"}`}>{t(emp.status) || emp.status}</span>
        </div>
      </Card>

      <Tabs defaultValue="contracts">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="contracts" data-testid="tab-contracts"><FileText className="w-4 h-4 me-2" />{t("contracts")}</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves"><CalendarOff className="w-4 h-4 me-2" />{t("leaves")}</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">{t("hr_attendance")}</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Coins className="w-4 h-4 me-2" />{t("payroll")}</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance"><Award className="w-4 h-4 me-2" />{t("performance")}</TabsTrigger>
          <TabsTrigger value="trainings" data-testid="tab-trainings"><GraduationCap className="w-4 h-4 me-2" />{t("hr_trainings")}</TabsTrigger>
          <TabsTrigger value="violations" data-testid="tab-violations"><AlertTriangle className="w-4 h-4 me-2" />{t("violations")}</TabsTrigger>
        </TabsList>

        <TabsContent value="contracts">
          <Card className="p-5 border shadow-none">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">{t("contracts")}</h3>
              <MiniDialog testIdPrefix="ct" title={t("new_btn")} onSubmit={addContract}
                trigger={<Button size="sm" data-testid="add-contract"><Plus className="w-4 h-4 me-1" /> {t("new_btn")}</Button>}
                fields={[
                  { name: "contract_no", label: t("contract_no_label") },
                  { name: "type", label: t("contract"), type: "select", options: [{ value: "permanent", label: t("permanent") }, { value: "temporary", label: t("temporary") }, { value: "probation", label: t("probation") }] },
                  { name: "start_date", label: t("start_date"), type: "date" },
                  { name: "end_date", label: t("end_date"), type: "date" },
                  { name: "salary", label: t("base_salary"), type: "number" },
                  { name: "document_file_id", label: t("contract") + " (PDF)", type: "file" },
                ]} />
            </div>
            <ul className="divide-y divide-border">
              {(emp.contracts || []).map(c => (
                <li key={c.id} className="py-2 text-sm flex justify-between items-start gap-3">
                  <div>
                    <div>{c.contract_no || "—"} · {t(c.type) || c.type}</div>
                    {c.document_file_id && <AttachmentList files={[{ id: c.document_file_id, name: t("contract") }]} />}
                  </div>
                  <span className="text-muted-foreground text-xs">{c.start_date} → {c.end_date || "—"} · {c.salary} {t("sar_unit")}</span>
                </li>
              ))}
              {(emp.contracts || []).length === 0 && <li className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</li>}
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="leaves">
          <Card className="p-5 border shadow-none">
            <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
              <div>
                <h3 className="font-semibold">{t("leaves")}</h3>
                {balance && <div className="text-xs text-muted-foreground mt-1">{t("leave_balance")} {balance.year} · {Object.entries(balance.balance).map(([k, v]) => `${t(k) || k}: ${v.remaining}/${v.allocated}`).join(" · ")}</div>}
              </div>
              <MiniDialog testIdPrefix="lv" title={t("request_leave")} onSubmit={addLeave}
                trigger={<Button size="sm" data-testid="add-leave"><Plus className="w-4 h-4 me-1" /> {t("request_leave")}</Button>}
                fields={[
                  { name: "leave_type", label: t("leave_type"), type: "select", options: [{ value: "annual", label: t("annual") }, { value: "sick", label: t("sick") }, { value: "emergency", label: t("emergency") }, { value: "unpaid", label: t("unpaid") }, { value: "maternity", label: t("maternity") }] },
                  { name: "start_date", label: t("start_date"), type: "date" },
                  { name: "end_date", label: t("end_date"), type: "date" },
                  { name: "reason", label: t("reason"), type: "textarea" },
                  { name: "attachment_file_id", label: t("attachment"), type: "file" },
                ]} />
            </div>
            <ul className="divide-y divide-border">
              {(emp.leaves || []).map(l => (
                <li key={l.id} className="py-2 text-sm flex justify-between items-center gap-2" data-testid={`leave-${l.id}`}>
                  <span>{t(l.leave_type)} · {l.start_date} → {l.end_date}</span>
                  <span className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === "approved" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30" : l.status === "rejected" ? "bg-destructive/10 text-destructive" : "bg-amber-100 text-amber-700 dark:bg-amber-900/30"}`}>{t(l.status)}</span>
                    {l.status === "pending" && <>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => decideLeave(l.id, "approved")} data-testid={`leave-approve-${l.id}`}>{t("approve")}</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => decideLeave(l.id, "rejected")} data-testid={`leave-reject-${l.id}`}>{t("reject")}</Button>
                    </>}
                  </span>
                </li>
              ))}
              {(emp.leaves || []).length === 0 && <li className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</li>}
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="attendance">
          <Card className="p-5 border shadow-none">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">{t("hr_attendance")}</h3>
              <MiniDialog testIdPrefix="att" title={t("hr_attendance")} onSubmit={addAttendance}
                trigger={<Button size="sm" data-testid="add-attendance"><Plus className="w-4 h-4 me-1" /> {t("new_btn")}</Button>}
                fields={[
                  { name: "date", label: t("date_label"), type: "date" },
                  { name: "check_in", label: t("check_in"), type: "time" },
                  { name: "check_out", label: t("check_out"), type: "time" },
                  { name: "status", label: t("status"), type: "select", options: [{ value: "present", label: t("present") }, { value: "late", label: t("late") }, { value: "absent", label: t("absent") }, { value: "leave", label: t("leave_label") }] },
                  { name: "late_min", label: t("delay_min"), type: "number" },
                ]} />
            </div>
            <p className="text-sm text-muted-foreground">{t("bulk_attendance_hint")}</p>
          </Card>
        </TabsContent>

        <TabsContent value="payroll">
          <Card className="p-5 border shadow-none">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">{t("payroll")}</h3>
              <MiniDialog testIdPrefix="pay" title={t("new_btn")} onSubmit={addPay}
                trigger={<Button size="sm" data-testid="add-pay"><Plus className="w-4 h-4 me-1" /> {t("new_btn")}</Button>}
                fields={[
                  { name: "period", label: t("period") + " (YYYY-MM)", type: "month" },
                  { name: "base_salary", label: t("base_salary"), type: "number" },
                  { name: "allowances_value", label: t("allowances"), type: "number" },
                  { name: "deductions_value", label: t("deductions"), type: "number" },
                  { name: "overtime", label: t("overtime"), type: "number" },
                ]} />
            </div>
            <ul className="divide-y divide-border">
              {(emp.payslips || []).map(p => (
                <li key={p.id} className="py-2 text-sm flex justify-between" data-testid={`pay-${p.id}`}>
                  <span className="font-mono">{p.period}</span>
                  <span>{t("gross")}: <b>{p.gross}</b> · {t("net")}: <b className="text-primary">{p.net}</b> {t("sar_unit")}</span>
                </li>
              ))}
              {(emp.payslips || []).length === 0 && <li className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</li>}
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card className="p-5 border shadow-none">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">{t("performance_reviews")}</h3>
              <MiniDialog testIdPrefix="rev" title={t("new_btn")} onSubmit={addReview}
                trigger={<Button size="sm" data-testid="add-review"><Plus className="w-4 h-4 me-1" /> {t("new_btn")}</Button>}
                fields={[
                  { name: "period", label: t("period") },
                  { name: "score", label: t("score") + " (0-5)", type: "number" },
                  { name: "strengths", label: t("strengths"), type: "textarea" },
                  { name: "improvements", label: t("improvements"), type: "textarea" },
                  { name: "goals", label: t("goals"), type: "textarea" },
                ]} />
            </div>
            <ul className="divide-y divide-border">
              {(emp.reviews || []).map(r => (
                <li key={r.id} className="py-2 text-sm flex justify-between" data-testid={`rev-${r.id}`}>
                  <span>{r.period}</span>
                  <span className="flex items-center gap-1 text-amber-500"><Star className="w-4 h-4 fill-current" />{r.score}/5</span>
                </li>
              ))}
              {(emp.reviews || []).length === 0 && <li className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</li>}
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="trainings">
          <Card className="p-5 border shadow-none">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">{t("hr_trainings")}</h3>
              <MiniDialog testIdPrefix="tr" title={t("new_btn")} onSubmit={addTraining}
                trigger={<Button size="sm" data-testid="add-training"><Plus className="w-4 h-4 me-1" /> {t("new_btn")}</Button>}
                fields={[
                  { name: "title", label: t("title_label") },
                  { name: "provider", label: t("provider") },
                  { name: "start_date", label: t("start_date"), type: "date" },
                  { name: "end_date", label: t("end_date"), type: "date" },
                  { name: "cost", label: t("cost"), type: "number" },
                  { name: "certificate_file_id", label: t("certificate"), type: "file" },
                ]} />
            </div>
            <ul className="divide-y divide-border">
              {(emp.trainings || []).map(tr => (
                <li key={tr.id} className="py-2 text-sm flex justify-between items-start gap-3" data-testid={`tr-${tr.id}`}>
                  <div>
                    <div>{tr.title}</div>
                    {tr.certificate_file_id && <AttachmentList files={[{ id: tr.certificate_file_id, name: t("certificate") }]} />}
                  </div>
                  <span className="text-muted-foreground text-xs">{tr.start_date} · {tr.cost} {t("sar_unit")}</span>
                </li>
              ))}
              {(emp.trainings || []).length === 0 && <li className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</li>}
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="violations">
          <Card className="p-5 border shadow-none">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">{t("violations")}</h3>
              <MiniDialog testIdPrefix="vi" title={t("new_btn")} onSubmit={addViolation}
                trigger={<Button size="sm" data-testid="add-violation"><Plus className="w-4 h-4 me-1" /> {t("new_btn")}</Button>}
                fields={[
                  { name: "date", label: t("date_label"), type: "date" },
                  { name: "type", label: t("type"), type: "select", options: [{ value: "warning", label: t("warning") }, { value: "written_warning", label: t("written_warning") }, { value: "suspension", label: t("suspension") }, { value: "fine", label: t("fine_amount") }, { value: "termination", label: t("termination") }] },
                  { name: "description", label: t("description"), type: "textarea" },
                  { name: "fine_amount", label: t("fine_amount"), type: "number" },
                ]} />
            </div>
            <ul className="divide-y divide-border">
              {(emp.violations || []).map(v => (
                <li key={v.id} className="py-2 text-sm flex justify-between" data-testid={`vi-${v.id}`}>
                  <span>{t(v.type) || v.type}</span>
                  <span className="text-muted-foreground">{v.date}{v.fine_amount ? ` · ${v.fine_amount} ${t("sar_unit")}` : ""}</span>
                </li>
              ))}
              {(emp.violations || []).length === 0 && <li className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</li>}
            </ul>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
